import ActionButton from 'components/ActionButton';
import TextInputField from 'components/TextInputField';
import { Controller, useForm } from 'react-hook-form';
import { LOGIN_EMAIL_PLACEHOLDER, loginValidationRules } from '../../constants';
import { LoginTabProps } from './types';
import { useEffect, useState } from 'react';
import { showError } from 'utils/toastNotifications';
import { AssetUrls } from 'config/assets';
import { sanitizeEmail } from 'utils/inputSanitization';
import PasswordResetModal from 'components/PasswordResetModal/PasswordResetModal';
import { useForgotPasswordMutation } from 'store/services/auth/authApi';
import './LoginTab.css';

const LOGIN_PASSWORD_PLACEHOLDER = 'Password';
const LOGIN_TITLE = 'Welcome Back';
const LOGIN_DESCRIPTION = 'Enter your credentials to login';

const LoginTab = ({ data, isLoading, error, login }: LoginTabProps) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    clearErrors,
  } = useForm({ defaultValues: { email: '', password: '' } });
  
  const [apiError, setApiError] = useState<any>(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [forgotPassword, { isLoading: isResetting }] = useForgotPasswordMutation();

  // Show toast notifications for errors only
  useEffect(() => {
    if (error) {
      // Provide more specific error messages based on the error
      let errorMessage = 'Login failed. Please try again.';
      
      if (error.status === 401) {
        errorMessage = 'Invalid email or password. Please check your credentials and try again.';
      } else if (error.status === 404) {
        errorMessage = 'No account found with this email address. Please sign up first.';
      } else if (error.data?.message) {
        errorMessage = error.data.message;
      }
      
      showError(errorMessage);
      setApiError(error); // Store the error to maintain red fields
    }
    // Don't show success notification - user knows they logged in when they reach the dashboard
  }, [error]);

  const onSubmit = (data: { email: string; password: string }) => {
    setApiError(null); // Clear any previous API errors
    // Sanitize inputs before submission
    const sanitizedEmail = sanitizeEmail(data.email);
    const sanitizedPassword = data.password; // Passwords should not be altered
    login(sanitizedEmail, sanitizedPassword);
  };

  const handleForgotPassword = async (email: string) => {
    await forgotPassword({ email }).unwrap();
  };

  return (
    <div className="login-tab">
      <div className="login-messages">
        <div className="login-title">
          {LOGIN_TITLE}
        </div>
        <div className="login-description">
          {LOGIN_DESCRIPTION}
        </div>
      </div>
      <div className="login-inputs-wrapper">
        <Controller
          name="email"
          control={control}
          rules={loginValidationRules.email}
          render={({ field }) => (
            <TextInputField
              label={LOGIN_EMAIL_PLACEHOLDER}
              error={Boolean(errors.email) || Boolean(apiError)}
              onChange={(value) => {
                // Sanitize email input
                const sanitized = sanitizeEmail(value);
                field.onChange(sanitized);
                // Clear errors when user starts typing
                if (errors.email) clearErrors('email');
                if (apiError) setApiError(null);
              }}
            />
          )}
        />
        <Controller
          name="password"
          control={control}
          rules={loginValidationRules.password}
          render={({ field }) => (
            <TextInputField
              label={LOGIN_PASSWORD_PLACEHOLDER}
              type="password"
              error={Boolean(errors.password) || Boolean(apiError)}
              onChange={(value) => {
                field.onChange(value);
                // Clear errors when user starts typing
                if (errors.password) clearErrors('password');
                if (apiError) setApiError(null);
              }}
            />
          )}
        />
        <div className="forgot-password-wrapper">
          <button
            type="button"
            className="forgot-password-link"
            onClick={() => setShowResetModal(true)}
          >
            Forgot Password?
          </button>
        </div>
        <ActionButton
          label="Sign In"
          icon={
            <img
              src={AssetUrls.logos.arevakhachIcon}
              alt="trademagic-icon"
            />
          }
          isLoading={isLoading}
          onPress={handleSubmit(onSubmit)}
        />
      </div>
      <PasswordResetModal
        isOpen={showResetModal}
        onClose={() => setShowResetModal(false)}
        onSubmit={handleForgotPassword}
        isLoading={isResetting}
      />
    </div>
  );
};

export default LoginTab;
